import { useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import moment from "moment";
import i18next from "_config/lang/i18n";
import { Calendar } from "react-date-range";
import * as locales from 'react-date-range/dist/locale';
import { InputLabel, InputTest, Select } from "components/molecules/Inputs";
import { Button } from "components/atoms";
import { useAppSettings, useForm, useTask } from "_hooks";
import { closeModal } from "_redux/ui/ui.actions";
import { resetPost, resetPut } from "_redux/task/task.actions";
import { selectUser } from "_redux/user/user.selectors";
import { selectTaskPost } from "_redux/task/task.selector";
import { sleep } from "_utils/async";
import { TYPE_GENERIC, TYPE_IMAGERY_REVIEW, TYPE_SHOOTING_SKU, TYPE_TEXT_REVIEW } from "_constants/task.constants";
import { LOADING } from "_constants/redux.constants";

const prefix = "o-task-form";

const TaskForm = ({ elements, onCancel, onFinished, task }) => {
  const { appColumnKeyProps } = useAppSettings();
  const dispatch = useDispatch();
  const { create, update } = useTask();
  const { t } = useTranslation();

  const [showCalendar, setShowCalendar] = useState(false);
  const [taskForm] = useState({});
  const [taskType, setTaskType] = useState();

  const inputRef = useRef(null);

  const { 
    changeHandler,
    disable: disabledForm, 
    error: errorForm, 
    form,
    setForm
  } = useForm({
    schemaType: "newTask",
    formInitialState: taskForm
  });

  const user = useSelector(selectUser);
  const { status: reqStatus } = useSelector(selectTaskPost);

  const submitHadler = async () => {
    let { date, description, name } = form;
    let response;

    if (task?._id) {
      response = await update(task._id, {
        name,
        type: taskType.value,
        data: {
          description
        },
        process: {
          deliveryDate: date || null
        }
      }, "taskAll");
    } else {
      response = await create({
        foldersId: elements,
        name,
        type: taskType.value,
        data: {
          description
        },
        process: {
          deliveryDate: date || null
        },
        userId: user._id,
        share: [],
        tasksId: []
      });
    }

    if (typeof onFinished === "function") onFinished(response?.data?.[0]);

    dispatch(closeModal());
    return response;
  };

  useEffect(() => {
    inputRef.current.focus();
    if (task) {
      let setValues = async () => {
        let { data, process, name, type } = task;
        await sleep(10).then(() => changeHandler({
          target: { name: "name", value: name }
        }));
        await sleep(10).then(() => changeHandler({
          target: { name: "description", value: data?.description }
        }));
        await sleep(10).then(() => changeHandler({target: { name: "date", value: process?.deliveryDate || null }}));
        
        let newData = {
          description: data?.description,
          date: process?.deliveryDate ? new Date(process.deliveryDate) : null,
          name
        };

        setTaskType({
          label: t(`tasks:newTask.types.${type?.toUpperCase()}`, { key: appColumnKeyProps?.name }),
          value: type
        });
        setForm(newData);
      }

      setValues();
    }
    
    return () => {
      if (task?._id) dispatch(resetPut());
      
      else dispatch(resetPost());
    };
  }, []);

  return (
    <div className={prefix}>
      <InputLabel label={t("tasks:newTask.fields.name.label")}>
        <InputTest
          onChange={changeHandler}
          name="name"
          ref={inputRef}
          error={errorForm?.name}
          value={form?.name}
        />
      </InputLabel>

      <InputLabel label={t("tasks:newTask.fields.description.label")}>
        <InputTest 
          minRows={3}
          name="description"
          onChange={changeHandler}
          error={errorForm?.description}
          value={form?.description}
        />
      </InputLabel>
      
      <div className={`${prefix}__flex-columns`}>
        <InputLabel label={t("tasks:newTask.fields.date.label")}>
          <InputTest 
            readOnly
            errors={errorForm?.date}
            name="date" 
            value={form?.date ? moment(form?.date).format("YYYY-MM-DD") : undefined}
            onFocus={() => setShowCalendar((v => !v))}
            onChange={(e) => {
              changeHandler(e);
              if (showCalendar) sleep(200).then(() => setShowCalendar(false))
            }}
          />
          {showCalendar && 
            <div className={`${prefix}__calendar-container`}>
              <Calendar
                direction="horizontal"
                onChange={(d) => {
                  setShowCalendar(false);
                  changeHandler({
                    target: { name: "date", value: d }
                  })
                }}
                date={form.date}
                dateDisplayFormat="YYYY-MM-DD"
                locale={locales?.[i18next.language]}
                dragSelectionEnabled={false}
                editableDateInputs={false}
              />
            </div>
          }
        </InputLabel>

        <InputLabel label={t("tasks:newTask.fields.type.label")}>
          <Select
            emptyLabel={t("tasks:newTask.fields.type.placeholder")}
            onChange={setTaskType}
            options={[{
              label: t("tasks:newTask.types.GENERIC_TASK"),
              value: TYPE_GENERIC
            }, {
              label: t("tasks:newTask.types.SHOOTING_SKU", { key: appColumnKeyProps?.name }),
              value: TYPE_SHOOTING_SKU
            }, {
              label: t("tasks:newTask.types.TEXT_REVIEW"),
              value: TYPE_TEXT_REVIEW
            }, {
              label: t("tasks:newTask.types.IMAGERY_REVIEW"),
              value: TYPE_IMAGERY_REVIEW
            }]}
            value={taskType}
          />
        </InputLabel>
      </div>

      <div className={`${prefix}__buttons`}>
        <Button 
          content={t("common:form.cancel")}
          onClick={onCancel}
          sm
          type="underline"
        />
        <Button 
          content={t(`tasks:newTask.${task?._id ? "save" : "create"}Btn`)}
          disabled={disabledForm || reqStatus === LOADING || !taskType}
          onClick={submitHadler}
          sm
          type="purple"
        />
      </div>
    </div>
  );
};

TaskForm.propTypes = {
  onCancel: PropTypes.func
};

TaskForm.defaultProps = {
  onCancel: () => {}
};

export default TaskForm;
